import { Component, Input, OnInit } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';

import { AccountService } from '../../../../../../services/account/account.service';
import { ScheduledEvent } from '../../../../../../models/scheduled-event';
import { CustomerService } from '../../../../../../services/customer/customer.service';
import { openClimberAppInNewTabAsCustomer, openClimberAppInNewTabAsStaff } from '../../../../../../utils-models';
import { OrganizationConfigService } from '../../../../../../services/organization-config/organization-config.service';
import { CustomerSearchResult } from '../../../../../../models/customer';
import {
  CallbackResult,
  CallbackResultStatus,
  dateStringFromISO8601,
  getISO8601DateStringFromMoment, propExists,
} from '../../../../../../utils';
import {
  BookEventDirectlyResponse,
  ScheduledEventService
} from '../../../../../../services/scheduled-event/scheduled-event.service';
import * as moment from 'moment';

@Component({
  selector: 'app-create-booking-modal',
  templateUrl: './create-booking-modal.component.html',
  styleUrls: ['./create-booking-modal.component.css']
})
export class CreateBookingModalComponent implements OnInit {

  @Input() scheduledEvent: ScheduledEvent;
  // checkInReservationPartId is the reservation part the opener should check
  // the participant into immediately, or null (PRO-1220).
  @Input() bookingCreatedCallback: (result: CallbackResult,
                                    checkInReservationPartId: number | null) => void;

  public bookViaClimberApp: boolean | null = null;

  public selectedNumParticipants = 1;
  public selectedCustomer: CustomerSearchResult = null;
  public sendConfirmationEmail = false;
  // Defaulted to checked when the event part is starting soon (PRO-1220).
  public checkInImmediately = false;
  // Defaulted from the event's pricing matrix, true only when every fee on it includes entry (PRO-1512).
  public includesEntry = false;

  public loadingSubmit = false;
  public loadingCustomers = false;

  public search = '';
  public customerFiltersChangedSubject = new Subject<null>();
  public customerList: CustomerSearchResult[] | null;

  public errorMessage = '';
  public serverError: number = null;

  public readonly dateStringFromISO8601 = dateStringFromISO8601;

  // The number of customer list requests. Used to ensure that only the most
  // recent request is displayed, in the event of concurrent requests (due to,
  // e.g., filtering).
  private customerListRequestCount = 0;

  private recentActivityFilter: string | undefined = undefined;

  constructor(
    public activeModal: NgbActiveModal,
    private customerService: CustomerService,
    private orgService: OrganizationConfigService,
    private accountService: AccountService,
    private scheduledEventService: ScheduledEventService
  ) {}

  ngOnInit() {
    // Handle changes to the customer filters by reloading the customers,
    // subject to debounce.
    this.customerFiltersChangedSubject.pipe(
      // Avoid rapid requests by waiting 300ms before applying the filters.
      debounceTime(300)
    ).subscribe(() => {
      this.loadCustomers();
    });

    // Within the hour or already started is when an immediate check-in is
    // most likely intended.
    if (this.scheduledEvent) {
      this.checkInImmediately = !this.startsMoreThanOneHourFromNow();
      this.includesEntry = this.scheduledEvent.direct_booking_includes_entry_default === true;
    }
  }

  /**
   * Whether the event part starts more than one hour from now. When true, the
   * "check in immediately" checkbox is not checked by default, and a warning is
   * shown if the user checks it anyway.
   */
  public startsMoreThanOneHourFromNow(): boolean {
    return moment(this.scheduledEvent.start_datetime).diff(moment(), 'hours', true) > 1;
  }

  /**
   * A human-readable description of how far in the future the event part starts,
   * e.g. "3 hours" or "2 days". Used in the warning shown when checking a
   * participant in well before the event starts.
   */
  public getTimeUntilEventDisplay(): string {
    const start = moment(this.scheduledEvent.start_datetime);
    const hoursFromNow = start.diff(moment(), 'hours', true);

    if (hoursFromNow < 24) {
      const hours = Math.max(1, Math.round(hoursFromNow));
      return hours === 1 ? '1 hour' : hours + ' hours';
    }

    const days = Math.round(start.diff(moment(), 'days', true));
    return days === 1 ? '1 day' : days + ' days';
  }

  public getNumParticipantsOptions() {
    const optionsList = [];
    for (let i = 1; i <= this.scheduledEvent.max_participant_count; i++) {
      optionsList.push(i);
    }
    return optionsList;
  }

  /**
   * Whether a non-zero number of customer results have been loaded.
   */
  public customerResultsPresent(): boolean {
    return (
      this.customerList &&
      (this.customerList.length > 0)
    );
  }

  public selectCustomer(customer: CustomerSearchResult) {
    this.selectedCustomer = customer;
    this.search = (
      customer.first_name + ' ' + customer.last_name
    );
  }

  /**
   * Handle a change in the customer filters. The list of customers will be
   * filtered according to the new values.
   */
  public handleCustomerFilterChange() {
    this.loadingCustomers = true;
    this.selectedCustomer = null;

    const orgConfig = this.orgService.getOrganizationConfig();
    const mustApplyRecentActivityFilter = (
      orgConfig &&
      orgConfig.customer_search_recent_activity_window_months
    );
    if (mustApplyRecentActivityFilter) {
      this.recentActivityFilter = getISO8601DateStringFromMoment(
        moment().subtract(orgConfig.customer_search_recent_activity_window_months, 'months')
      );
    }

    this.customerFiltersChangedSubject.next();
  }

  public handleGoToScheduler() {
    this.loadingSubmit = true;
    this.errorMessage = '';

    if (this.selectedCustomer === null) {
      this.customerService
        .getStaffClimberAppToken()
        .then(token => {
          openClimberAppInNewTabAsStaff(
            token,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.scheduledEvent.event_scheduler_url
              + '?use_saved_response=false&num_participants='
              + this.selectedNumParticipants.toString()
            )
          );
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to create this booking.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    } else {
      this.customerService
        .getCustomerClimberAppToken(this.selectedCustomer.id)
        .then(token => {
          openClimberAppInNewTabAsCustomer(
            token,
            this.selectedCustomer.id,
            this.orgService.getOrganizationConfig().climber_app_base_url,
            (
              this.scheduledEvent.event_scheduler_url
              + '?use_saved_response=false&num_participants='
              + this.selectedNumParticipants.toString()
            )
          );
          this.activeModal.close();
        })
        .catch(reason => {
          if (reason.status === 403) {
            this.errorMessage = 'You do not have permission to create this booking.';
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => {
          this.loadingSubmit = false;
        });
    }
  }

  public handleCreateBooking() {
    if (!this.selectedCustomer) { return; }

    this.loadingSubmit = true;
    this.errorMessage = '';

    this.scheduledEventService
      .bookCustomerOntoEventById(
        this.scheduledEvent.event_id, this.selectedCustomer.id, this.sendConfirmationEmail, this.includesEntry
      )
      .then(response => {
        // The opener checks the participant in exactly as its "Check In"
        // button would, facility check-in included (PRO-1220).
        const checkInReservationPartId = this.checkInImmediately
          ? this.getReservationPartIdForEventPart(response)
          : null;

        this.bookingCreatedCallback(
          {
            text: 'Booking created successfully.',
            status: CallbackResultStatus.success
          },
          checkInReservationPartId
        );
        this.activeModal.close();
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.errorMessage = 'You do not have permission to create this booking.';
        } else if (reason.status === 400) {
          this.errorMessage = reason.error;
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingSubmit = false;
      });
  }

  /**
   * Find the reservation part ID corresponding to the event part shown in this
   * modal, from a direct-booking response. Returns null if absent (shouldn't
   * happen in practice, but guards the immediate check-in).
   */
  private getReservationPartIdForEventPart(response: BookEventDirectlyResponse): number | null {
    const match = response.reservation_parts.find(
      part => part.event_part_id === this.scheduledEvent.event_part_id
    );
    return match ? match.reservation_part_id : null;
  }

  public hasSearchInput() {
    return this.search.trim().length >= 3;
  }

  public hasRecentActivityFilter(): boolean {
    return propExists(this.recentActivityFilter);
  }

  public handleExpandSearch() {
    this.recentActivityFilter = undefined;
    this.loadingCustomers = true;
    this.customerFiltersChangedSubject.next();
  }

  /**
   * Load the page of customers corresponding to the current page and filter
   * selections.
   */
  private loadCustomers() {
    this.loadingCustomers = true;

    // Increment the request count and save a local copy.
    this.customerListRequestCount++;
    const thisRequestCount = this.customerListRequestCount;

    if (!this.hasSearchInput()) {
      this.customerList = null;
      this.loadingCustomers = false;
      return;
    }

    this.customerService
      .searchCustomers(
        1,
        200,
        {
          search: this.search,
          last_activity_timestamp_min: this.recentActivityFilter,
        },
        true
      )
      .then(response => {
        // Only update the customer list if there has not been a more recent
        // request initiated.
        if (thisRequestCount === this.customerListRequestCount) {
          this.customerList = response.results;
        }
      })
      .catch(reason => {
        if (reason.status === 403) {
          this.accountService.logout();
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        if (thisRequestCount === this.customerListRequestCount) {
          this.loadingCustomers = false;
        }
      });
  }

}
